import React from "react";
import { useParams, Link, Navigate } from "react-router-dom";
import { PageShell } from "@/components/PageShell";
import { Button } from "@/components/ui/button";
import { CheckCircle2 } from "lucide-react";

const CONTENT = {
  "ai-analyzer": {
    title: "AI Chart Analyzer",
    tagline: "Institutional-grade chart reads, on demand.",
    intro: "Upload any candlestick screenshot and our Claude Sonnet 5.5 vision engine returns a full trade plan — bias, market structure, key levels, Fibonacci confluence, entry, stop loss, two targets and risk:reward.",
    points: ["Reads market structure, liquidity sweeps and order blocks", "4 strategy engines: Scalping, Swing, SMC, Breakout", "Actionable entry / SL / TP with risk:reward", "Available inside your membership dashboard"],
  },
  "trade-journal": {
    title: "Trade Journal",
    tagline: "Track every trade. Improve every day.",
    intro: "Log trades in seconds, record your emotions before, during and after each trade, attach screenshots, and get instant AI coaching that turns every trade into a lesson.",
    points: ["Quick trade entry with emotion tracking", "AI-generated insights on execution & psychology", "Advanced filtering by pair, session and result", "Performance patterns that reveal your edge"],
  },
  "forex-tools": {
    title: "Forex Tools",
    tagline: "Size every position like an institution.",
    intro: "Precision calculators powered by live exchange rates. Work out lot size, pip value and cash-at-risk instantly, and convert between currencies in real time.",
    points: ["Pip & risk / position-size calculator", "Live-rate currency converter", "Built for disciplined risk management", "Available inside your dashboard"],
  },
  "community": {
    title: "Community Floor",
    tagline: "Trade alongside serious traders.",
    intro: "Share setups, debate macro and learn from an elite trader community. Post ideas with pair and sentiment tags, react and discuss in real time — with notifications when others engage.",
    points: ["Create posts with pair & sentiment tags", "Likes, comments and reactions", "Real-time notifications", "Filter the feed by topic"],
  },
  "curriculum": {
    title: "Academy Curriculum",
    tagline: "From zero to funded — a structured path.",
    intro: "A step-by-step curriculum built by desk traders. Progress from foundations to professional risk management with real-market examples.",
    points: ["Beginner: building your forex foundation", "Intermediate: mastering market analysis", "Advanced: professional trading & risk", "Lifetime access — revisit any lesson"],
  },
  "market-structure": {
    title: "Market Structure Mastery",
    tagline: "Read the market like a professional.",
    intro: "Learn to identify higher highs and lows, break of structure and change of character across timeframes so you always trade with the dominant flow.",
    points: ["HH/HL and LH/LL identification", "Break of structure (BOS) & CHoCH", "Multi-timeframe alignment", "Trend vs range recognition"],
  },
  "smc": {
    title: "Smart Money Concepts",
    tagline: "Trade with the institutions, not against them.",
    intro: "Master order blocks, fair value gaps, liquidity sweeps and institutional footprints to find high-probability, low-risk entries.",
    points: ["Order blocks & mitigation", "Fair value gaps (FVG)", "Liquidity sweeps & inducement", "Premium/discount zones"],
  },
  "psychology": {
    title: "Trading Psychology",
    tagline: "Engineer the discipline of a pro.",
    intro: "Your edge is meaningless without discipline. Build the mindset to manage fear, greed, FOMO and revenge trading — and let your journal keep you accountable.",
    points: ["Beat overtrading, FOMO & revenge trades", "Rules-based risk management", "Emotion tracking that drives improvement", "Consistency over intensity"],
  },
  "about": {
    title: "About Netroy",
    tagline: "Built by traders, for serious traders.",
    intro: "Netroy Forex Academy is a complete trading operating system — AI chart intelligence, a smart trade journal, precision risk tools and an elite community — engineered to help traders grow with structure and discipline.",
    points: ["AI-first, education-led platform", "Focused on risk and psychology", "A growing global community", "Transparent, membership-based pricing"],
  },
  "careers": {
    title: "Careers",
    tagline: "Help us build the future of trader education.",
    intro: "We're a small, fast-moving team obsessed with helping traders improve. We hire for curiosity, craft and care. Open roles are added here as we grow.",
    points: ["Remote-first culture", "Trading, engineering & content roles", "Ship fast, learn faster", "Reach out via the contact page"],
    ctaLabel: "Contact us", ctaTo: "/contact",
  },
  "blog": {
    title: "Netroy Blog",
    tagline: "Insights, breakdowns and market notes.",
    intro: "Our trading desk shares setups, macro breakdowns and lessons from the market. New articles are published here regularly.",
    points: ["Weekly market breakdowns", "Strategy deep-dives", "Psychology & risk essays", "Platform updates"],
  },
};

export default function InfoPage() {
  const { slug } = useParams();
  const page = CONTENT[slug];
  if (!page) return <Navigate to="/" replace />;

  return (
    <PageShell>
      <section className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 py-16" data-testid={`info-page-${slug}`}>
        <span className="font-data text-xs uppercase tracking-[0.2em] text-brand">◆ {page.tagline}</span>
        <h1 className="mt-3 font-heading text-4xl sm:text-5xl font-black tracking-tight">{page.title}</h1>
        <p className="mt-5 text-slate-300 text-lg leading-relaxed">{page.intro}</p>

        <ul className="mt-8 space-y-3">
          {page.points.map((p) => (
            <li key={p} className="flex items-start gap-3 text-slate-300">
              <CheckCircle2 className="h-5 w-5 text-brand mt-0.5 shrink-0" /> <span>{p}</span>
            </li>
          ))}
        </ul>

        <div className="mt-10 flex flex-wrap gap-3">
          <Link to={page.ctaTo || "/#pricing"}>
            <Button className="bg-brand hover:bg-brand-hover text-white font-semibold h-11 px-6">{page.ctaLabel || "Get Started Free"}</Button>
          </Link>
          <Link to="/contact">
            <Button variant="outline" className="bg-transparent border-white/15 text-white hover:bg-white/5 h-11 px-6">Contact Us</Button>
          </Link>
        </div>
      </section>
    </PageShell>
  );
}
